import { useState, useEffect, useCallback, useRef } from "react";
import DailyIframe from "@daily-co/daily-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Video, VideoOff, Mic, MicOff, PhoneOff, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";

const VideoCall = ({ roomUrl, token, userName, isOwner, onLeave, onCallEnd, onTranscriptUpdate }) => {
  const [callFrame, setCallFrame] = useState(null);
  const [isJoined, setIsJoined] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("Initializing...");
  
  // Transcription state - OFF by default for privacy
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isTranscriptionPaused, setIsTranscriptionPaused] = useState(false);
  const [transcriptMessages, setTranscriptMessages] = useState([]);
  const transcriptRef = useRef([]);
  
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const fullscreenTargetRef = useRef(null);
  const mountedRef = useRef(true);

  // Keep React state in sync with the actual browser fullscreen state
  // (e.g., when the user presses ESC to exit fullscreen).
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!fsEl);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Ensure frame is destroyed on unmount
      if (frameRef.current) {
        try {
          frameRef.current.destroy();
        } catch (e) {
          console.log("Frame cleanup on unmount:", e);
        }
        frameRef.current = null;
      }
    };
  }, []);

  // Send transcript updates to parent component
  useEffect(() => {
    if (onTranscriptUpdate && transcriptMessages.length > 0) {
      onTranscriptUpdate(transcriptMessages);
    }
  }, [transcriptMessages, onTranscriptUpdate]);

  useEffect(() => {
    // Skip if not mounted, already have a frame, container missing, or ended
    if (!mountedRef.current || !containerRef.current || frameRef.current || isEnded) return;

    const frame = DailyIframe.createFrame(containerRef.current, {
      showLeaveButton: false,
      showFullscreenButton: true,
      iframeStyle: {
        width: "100%",
        height: "100%",
        border: "0",
        borderRadius: "0.5rem",
      },
    });

    frameRef.current = frame;

    frame.on("loading", () => {
      setConnectionStatus("Loading video room...");
    });

    frame.on("loaded", () => {
      setConnectionStatus("Joining call...");
    });

    frame.on("joining-meeting", () => {
      setConnectionStatus("Joining meeting...");
    });

    frame.on("joined-meeting", async () => {
      setIsJoined(true);
      setConnectionStatus("Connected");
      toast.success("Connected to video call");
    });

    frame.on("left-meeting", () => {
      setIsJoined(false);
      setConnectionStatus("Disconnected");
      setIsEnded(true);
    });

    frame.on("participant-joined", (event) => {
      setParticipants((prev) => [...prev, event.participant]);
      if (event.participant.user_name) {
        toast.info(`${event.participant.user_name} joined the call`);
      }
    });

    frame.on("participant-left", (event) => {
      setParticipants((prev) => prev.filter((p) => p.session_id !== event.participant.session_id));
      
      // If the owner left (therapist ended the call), end the call for everyone
      if (event.participant.owner) {
        setIsEnded(true);
        setConnectionStatus("Call ended by host");
        toast.info("The host has ended the call.");
        if (onCallEnd) onCallEnd(transcriptRef.current);
      }
    });

    // Handle when the meeting is ended by the owner
    frame.on("meeting-ended", () => {
      setIsEnded(true);
      setConnectionStatus("Meeting ended");
      toast.info("The video call has ended.");
      if (onCallEnd) onCallEnd(transcriptRef.current);
    });

    // ========== TRANSCRIPTION EVENTS ==========
    
    // Transcription started event
    frame.on("transcription-started", (event) => {
      console.log("Transcription started:", event);
      setIsTranscribing(true);
      toast.success("Live transcription active", { duration: 2000 });
    });

    // Transcription stopped event
    frame.on("transcription-stopped", (event) => {
      console.log("Transcription stopped:", event);
      setIsTranscribing(false);
    });

    // Transcription error event
    frame.on("transcription-error", (event) => {
      console.error("Transcription error:", event);
      setIsTranscribing(false);
    });

    // Real-time transcription message event - THIS IS THE KEY EVENT
    frame.on("transcription-message", (event) => {
      console.log(">>> TRANSCRIPTION MESSAGE RECEIVED:", event);
      
      if (event.text && event.text.trim()) {
        const message = {
          text: event.text,
          timestamp: event.timestamp || new Date().toISOString(),
          participantId: event.participantId,
          speaker: event.user_name || event.participantId || "Speaker",
        };
        
        // Update ref immediately (for when call ends)
        transcriptRef.current = [...transcriptRef.current, message];
        
        // Update state for UI updates
        setTranscriptMessages(prev => [...prev, message]);
        console.log(`Transcript count now: ${transcriptRef.current.length}`);
      }
    });
    
    // Also try listening to receive-transcription-message (alternate event name)
    frame.on("receive-transcription-message", (event) => {
      console.log(">>> RECEIVE-TRANSCRIPTION-MESSAGE:", event);
      if (event.text && event.text.trim()) {
        const message = {
          text: event.text,
          timestamp: event.timestamp || new Date().toISOString(),
          participantId: event.participantId,
          speaker: event.user_name || event.participantId || "Speaker",
        };
        transcriptRef.current = [...transcriptRef.current, message];
        setTranscriptMessages(prev => [...prev, message]);
      }
    });

    // Listen for ALL events to debug what's coming through
    frame.on("*", (event) => {
      if (event && event.action && event.action.toLowerCase().includes("transcript")) {
        console.log(">>> TRANSCRIPT-RELATED EVENT:", event);
      }
    });

    // Also listen for app-message in case transcription comes through there
    frame.on("app-message", (event) => {
      if (event.data?.type === "transcription" && event.data?.text) {
        const message = {
          text: event.data.text,
          timestamp: event.data.timestamp || new Date().toISOString(),
          participantId: event.data.participantId,
          speaker: event.data.user_name || "Speaker",
        };
        transcriptRef.current = [...transcriptRef.current, message];
        setTranscriptMessages(prev => [...prev, message]);
      }
    });

    // ========== END TRANSCRIPTION EVENTS ==========

    frame.on("error", (event) => {
      console.error("Daily.co error:", event);
      setConnectionStatus("Error");
      setIsEnded(true);
      // Show user-friendly message for common errors
      const errorMsg = event.errorMsg || event.error || "Connection lost";
      if (errorMsg.includes("not found") || errorMsg.includes("deleted") || errorMsg.includes("expired")) {
        toast.info("The video call has ended.");
      } else {
        toast.error(`Video call error: ${errorMsg}`);
      }
    });

    // Handle network errors (room deleted while connected)
    frame.on("nonfatal-error", (event) => {
      console.log("Daily.co non-fatal error:", event);
      // Room was likely deleted, end gracefully
      if (event.type === "ejected") {
        setIsEnded(true);
        setConnectionStatus("Disconnected");
        toast.info("The video call has ended.");
      }
    });

    setCallFrame(frame);

    // Join the meeting with token
    setConnectionStatus("Connecting to video room...");
    frame.join({ 
      url: roomUrl, 
      token: token,
      userName: userName 
    }).catch(err => {
      console.error("Join error:", err);
      toast.error("Failed to join video call");
      setIsEnded(true);
    });

    // Cleanup function
    return () => {
      if (frameRef.current) {
        try {
          frameRef.current.destroy();
        } catch (e) {
          console.log("Frame already destroyed");
        }
        frameRef.current = null;
      }
    };
  }, [roomUrl, token, userName]);

  const toggleCamera = async () => {
    if (callFrame && isJoined) {
      await callFrame.setLocalVideo(!isCameraOn);
      setIsCameraOn(!isCameraOn);
    }
  };

  const toggleMic = async () => {
    if (callFrame && isJoined) {
      await callFrame.setLocalAudio(!isMicOn);
      setIsMicOn(!isMicOn);
    }
  };

  const leaveCall = async () => {
    // CRITICAL: Save transcript reference FIRST before any cleanup
    const savedTranscript = [...transcriptRef.current];
    console.log(`LeaveCall: Saving ${savedTranscript.length} transcript messages before cleanup`);
    
    // Stop transcription before leaving
    if (callFrame && isTranscribing) {
      try {
        await callFrame.stopTranscription();
      } catch (e) {
        console.log("Error stopping transcription:", e);
      }
    }
    
    setIsEnded(true);
    
    // Send transcript to parent BEFORE cleanup
    if (onLeave) {
      console.log(`LeaveCall: Calling onLeave with ${savedTranscript.length} messages`);
      try {
        onLeave(savedTranscript);
      } catch (e) {
        console.error("Error in onLeave callback:", e);
      }
    }
    
    // Now cleanup
    if (callFrame) {
      try {
        await callFrame.leave();
        callFrame.destroy();
      } catch (e) {
        console.log("Error leaving call:", e);
      }
    }
    frameRef.current = null;
    setCallFrame(null);
  };

  const endCall = async () => {
    // CRITICAL: Save transcript reference FIRST before any cleanup
    const savedTranscript = [...transcriptRef.current];
    console.log(`EndCall: Saving ${savedTranscript.length} transcript messages before cleanup`);
    
    // Stop transcription before ending
    if (callFrame && isTranscribing) {
      try {
        await callFrame.stopTranscription();
        console.log("Transcription stopped successfully");
      } catch (e) {
        console.log("Error stopping transcription:", e);
      }
    }
    
    setIsEnded(true);
    
    // Send transcript to parent BEFORE cleanup to ensure it's not lost
    if (onCallEnd) {
      console.log(`EndCall: Calling onCallEnd with ${savedTranscript.length} messages`);
      try {
        onCallEnd(savedTranscript);
      } catch (e) {
        console.error("Error in onCallEnd callback:", e);
      }
    }
    
    // Now cleanup the call frame
    if (callFrame) {
      try {
        await callFrame.leave();
        callFrame.destroy();
      } catch (e) {
        console.log("Error ending call:", e);
      }
    }
    frameRef.current = null;
    setCallFrame(null);
  };

  const toggleFullscreen = async () => {
    const target = fullscreenTargetRef.current;
    if (!target) {
      setIsFullscreen((prev) => !prev);
      return;
    }

    const fsEl =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;

    try {
      if (!fsEl) {
        const request =
          target.requestFullscreen ||
          target.webkitRequestFullscreen ||
          target.msRequestFullscreen;
        if (request) {
          await request.call(target);
          // state will be updated via fullscreenchange listener
          return;
        }
      } else {
        const exit =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.msExitFullscreen;
        if (exit) {
          await exit.call(document);
          return;
        }
      }
    } catch (err) {
      console.warn("Native fullscreen failed, falling back to CSS overlay:", err);
    }

    // Fallback: CSS overlay (older browsers / blocked permissions)
    setIsFullscreen((prev) => !prev);
  };

  // ========== TRANSCRIPTION CONTROLS (Therapist Only) ==========
  
  const startTranscription = async () => {
    if (!callFrame || !isJoined) {
      toast.error("Must be connected to start transcription");
      return;
    }
    
    try {
      console.log("Starting transcription...");
      
      if (typeof callFrame.startTranscription !== 'function') {
        console.error("startTranscription method not available");
        toast.error("Transcription not available on this call");
        return;
      }
      
      await callFrame.startTranscription({
        language: "en",
        model: "nova-2",
        profanity_filter: false,
        punctuate: true,
        includeRawResponse: true,
      });
      
      setIsTranscribing(true);
      setIsTranscriptionPaused(false);
      toast.success("Transcription started - Recording in progress", { duration: 3000 });
      console.log("Transcription started successfully");
    } catch (err) {
      console.error("Failed to start transcription:", err);
      toast.error(`Failed to start transcription: ${err?.message || 'Unknown error'}`);
    }
  };

  const stopTranscription = async () => {
    if (!callFrame) return;
    
    try {
      await callFrame.stopTranscription();
      setIsTranscribing(false);
      setIsTranscriptionPaused(false);
      toast.info(`Transcription stopped - ${transcriptRef.current.length} messages recorded`, { duration: 3000 });
      console.log("Transcription stopped");
    } catch (err) {
      console.error("Failed to stop transcription:", err);
      toast.error("Failed to stop transcription");
    }
  };

  const pauseTranscription = async () => {
    if (!callFrame || !isTranscribing) return;
    
    try {
      // Daily.co doesn't have a native pause - we stop and track state
      await callFrame.stopTranscription();
      setIsTranscriptionPaused(true);
      setIsTranscribing(false);
      toast.info("Transcription paused - Private conversation mode", { duration: 3000 });
      console.log("Transcription paused");
    } catch (err) {
      console.error("Failed to pause transcription:", err);
      toast.error("Failed to pause transcription");
    }
  };

  const resumeTranscription = async () => {
    if (!callFrame || !isTranscriptionPaused) return;
    
    try {
      await callFrame.startTranscription({
        language: "en",
        model: "nova-2",
        profanity_filter: false,
        punctuate: true,
        includeRawResponse: true,
      });
      
      setIsTranscribing(true);
      setIsTranscriptionPaused(false);
      toast.success("Transcription resumed", { duration: 2000 });
      console.log("Transcription resumed");
    } catch (err) {
      console.error("Failed to resume transcription:", err);
      toast.error("Failed to resume transcription");
    }
  };

  // ========== END TRANSCRIPTION CONTROLS ==========

  // Get the current transcript as formatted text
  const getFormattedTranscript = () => {
    return transcriptRef.current
      .map(msg => `[${new Date(msg.timestamp).toLocaleTimeString()}] ${msg.speaker}: ${msg.text}`)
      .join("\n");
  };

  // If call has ended, show ended state
  if (isEnded) {
    return (
      <Card className="w-full">
        <CardContent className="p-12 text-center">
          <VideoOff className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Video Call Ended</h3>
          <p className="text-muted-foreground">The video session has been closed.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      ref={fullscreenTargetRef}
      className={`${isFullscreen ? "fixed inset-0 z-50 rounded-none bg-background" : "w-full"}`}
      data-testid="video-call-root"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">Video Session</CardTitle>
            {isJoined ? (
              <Badge variant="default" className="bg-green-500">Live</Badge>
            ) : (
              <Badge variant="secondary">{connectionStatus}</Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              data-testid="fullscreen-toggle"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Video Container - Daily.co handles its own UI */}
        <div 
          ref={containerRef}
          className={`relative bg-slate-900 rounded-lg overflow-hidden ${isFullscreen ? "h-[calc(100vh-180px)]" : "h-[500px]"}`}
          data-testid="video-container"
        />

        {/* Main Call Controls */}
        <div className="flex items-center justify-center gap-4 mt-4">
          <Button
            variant={isCameraOn ? "outline" : "destructive"}
            size="lg"
            onClick={toggleCamera}
            className="rounded-full w-12 h-12 p-0"
            data-testid="toggle-camera"
            disabled={!isJoined}
          >
            {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </Button>
          
          <Button
            variant={isMicOn ? "outline" : "destructive"}
            size="lg"
            onClick={toggleMic}
            className="rounded-full w-12 h-12 p-0"
            data-testid="toggle-mic"
            disabled={!isJoined}
          >
            {isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </Button>

          <Button
            variant="destructive"
            size="lg"
            onClick={isOwner ? endCall : leaveCall}
            className="rounded-full w-14 h-14 p-0"
            data-testid="leave-call"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>

        {/* Status text */}
        <div className="text-center text-sm text-muted-foreground mt-3">
          {isJoined ? (
            <span>{participants.length + 1} participant(s) in call</span>
          ) : (
            <span>{connectionStatus}</span>
          )}
        </div>

        {/* Hint for therapist */}
        {isOwner && isJoined && (
          <div className="text-center text-xs text-muted-foreground mt-2">
            <p>Click the red phone button to end the call for everyone</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VideoCall;
